import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Pencil, Trash2, AlertTriangle, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ProductFormDialog } from "@/components/ProductFormDialog";
import { api, errMsg, formatRupiah, imageUrl } from "@/lib/api";

const StockBadge = ({ p }) => (
  <span
    data-testid={`product-stock-badge-${p.id}`}
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-xs font-bold ${
      p.stock === 0 ? "bg-red-100 text-red-700" : p.is_low_stock ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-700"
    }`}
  >
    {p.is_low_stock && <AlertTriangle className="h-3 w-3" />}
    {p.stock}
  </span>
);

export default function Produk() {
  const [params, setParams] = useSearchParams();
  const lowOnly = params.get("filter") === "low";
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category))], [products]);
  const lowCount = products.filter((p) => p.is_low_stock).length;
  const rows = products.filter(
    (p) => (!lowOnly || p.is_low_stock) && `${p.name} ${p.sku} ${p.category}`.toLowerCase().includes(search.toLowerCase())
  );

  const openForm = (p = null) => { setEditing(p); setFormOpen(true); };
  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleting.id}`);
      toast.success("Produk dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDeleting(null);
  };

  return (
    <div className="space-y-6" data-testid="produk-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">Produk & Stok</h1>
          <p className="mt-1 text-sm text-slate-500">{products.length} produk terdaftar</p>
        </div>
        <Button onClick={() => openForm()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-product-button">
          <Plus className="mr-2 h-4 w-4" /> Tambah Produk
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari produk..." className="h-10 bg-white pl-9" data-testid="product-search-input" />
        </div>
        <div className="flex rounded-full bg-white p-1 ring-1 ring-slate-200">
          <button onClick={() => setParams({})} data-testid="product-filter-all" className={`rounded-full px-4 py-1.5 text-sm font-semibold ${!lowOnly ? "bg-slate-900 text-white" : "text-slate-600"}`}>Semua</button>
          <button onClick={() => setParams({ filter: "low" })} data-testid="product-filter-low" className={`rounded-full px-4 py-1.5 text-sm font-semibold ${lowOnly ? "bg-amber-500 text-white" : "text-amber-700"}`}>
            Stok Menipis ({lowCount})
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produk</TableHead>
              <TableHead>Kategori</TableHead>
              <TableHead className="text-right">Harga Jual</TableHead>
              <TableHead className="text-right">Modal</TableHead>
              <TableHead className="text-center">Stok</TableHead>
              <TableHead className="text-center">Min.</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`} className={p.is_low_stock ? "bg-amber-50/40" : ""}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 text-slate-300">
                      {p.image_path ? <img src={imageUrl(p.image_path)} alt={p.name} loading="lazy" className="h-full w-full object-cover" data-testid={`product-thumb-${p.id}`} /> : <ImageOff className="h-4 w-4" />}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">{p.name}</p>
                      <p className="font-mono text-xs text-slate-400">{p.sku || "-"}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-slate-600">{p.category}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-emerald-700">{formatRupiah(p.price)}</TableCell>
                <TableCell className="text-right font-mono text-slate-500">{formatRupiah(p.cost)}</TableCell>
                <TableCell className="text-center"><StockBadge p={p} /></TableCell>
                <TableCell className="text-center font-mono text-slate-500">{p.min_stock}</TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openForm(p)} data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => setDeleting(p)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-slate-500" data-testid="product-empty">Tidak ada produk.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <ProductFormDialog open={formOpen} product={editing} categories={categories} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus produk?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.name}" akan dihapus permanen. Riwayat transaksi tetap tersimpan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="product-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
